import type { InferGetServerSidePropsType, NextPage } from 'next';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

import { Weather } from '#components/Weather/Weather';
import { WeatherError } from '#components/Weather/WeatherError';
import { NAVIGATION_ITEM_WEATHER } from '#config/constants';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { NoSSR } from '#cutils/misc/NoSSR';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { buildContentPageSeoConfig } from '#cutils/seo/buildContentPageSeoConfig';
import { envConfigServer } from '#sconfig/env.server';
import { useTracktor } from 'react-tracktor';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const SEO_DESCRIPTION =
	'Das aktuelle Wetter in Bayern und seinen Regionen mit der Vorhersage für heute Abend, heute Nacht, morgen und die nächsten Tage mit Karten, Tabellen und Wetterbericht für heute sowie den Wetteraussichten. ';
const SEO_TITLE = 'Bayern-Wetter';

const Page: NextPage<Props> = ({ isEnabled }) => {
	const environment = useEnvironment();
	useTracktor({ pageViewData: { data: NAVIGATION_ITEM_WEATHER, event: { type: 'PageViewEvent' } } });

	const SEO_URL = `${environment.constants.appUrl}/bayern-wetter`;
	const seoConfig = buildContentPageSeoConfig({ description: SEO_DESCRIPTION, title: SEO_TITLE, url: SEO_URL });

	if (isEnabled) {
		return (
			<>
				<Head>{generateNextSeo(seoConfig)}</Head>

				<NoSSR>
					<Weather />
				</NoSSR>
			</>
		);
	} else {
		return (
			<>
				<Head>{generateNextSeo(seoConfig)}</Head>

				<WeatherError />
			</>
		);
	}
};

export const getServerSideProps = createDefaultGetServerSideProps(() => {
	return {
		props: {
			navigationTitle: NAVIGATION_ITEM_WEATHER.pageTitle,
			isEnabled: !envConfigServer.IS_WEATHER_HIDDEN,
			cacheTags: new Set<CacheTag>(),
		},
	};
});

export default Page;
